import { Result, ok, err } from '@nx-paeonia/utils-result';

export type ApiError =
  | { type: 'NetworkError'; message: string }
  | { type: 'ParseError'; message: string }
  | { type: 'HttpError'; status: number; message: string }
  | { type: 'ValidationError'; errors: Record<string, string[]> };

export interface RequestOptions extends RequestInit {
  timeout?: number;
  retries?: number;
}

export const shopifyFetch = async <T>(url: string, options: RequestOptions = {}): Promise<Result<T, ApiError>> => {
  const { timeout = 10000, retries = 0, ...fetchOptions } = options;

  for (let attempt = 0; attempt <= retries; attempt++) {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), timeout);

      const headers = new Headers(fetchOptions.headers);

      if (!(fetchOptions?.body instanceof FormData)) {
        headers.set('X-Requested-With', 'XMLHttpRequest');
        headers.set('Content-Type', 'application/json');
        headers.set('Accept', 'application/json');
      }

      const response = await fetch(url, {
        ...fetchOptions,
        signal: controller.signal,
        headers,
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));

        // Handle Shopify validation errors
        if (response.status === 422 && errorData.errors) {
          return err({
            type: 'ValidationError',
            errors: errorData.errors,
          });
        }

        return err({
          type: 'HttpError',
          status: response.status,
          message: errorData.message || response.statusText,
        });
      }

      const data = await response.json();
      return ok(data as T);
    } catch (error) {
      if (attempt === retries) {
        if (error instanceof Error) {
          if (error.name === 'AbortError') {
            return err({ type: 'NetworkError', message: 'Request timeout' });
          }
          return err({ type: 'NetworkError', message: error.message });
        }
        return err({ type: 'NetworkError', message: 'Unknown network error' });
      }
      // Retry logic - wait before next attempt
      await new Promise((resolve) => setTimeout(resolve, 1000 * (attempt + 1)));
    }
  }

  return err({ type: 'NetworkError', message: 'Max retries exceeded' });
};

export const shopifyPost = <T>(url: string, data: unknown, options?: RequestOptions) =>
  shopifyFetch<T>(url, { method: 'POST', body: JSON.stringify(data), ...options });

export const shopifyPostFormData = <T>(url: string, formData: FormData, options?: RequestOptions) =>
  shopifyFetch<T>(url, {
    method: 'POST',
    body: formData,
    ...options,
  });

export const shopifyGet = <T>(url: string, options?: RequestOptions) => shopifyFetch<T>(url, { method: 'GET', ...options });

export const shopifyPut = <T>(url: string, data: unknown, options?: RequestOptions) =>
  shopifyFetch<T>(url, {
    method: 'PUT',
    body: JSON.stringify(data),
    ...options,
  });

export const shopifyDelete = <T>(url: string, options?: RequestOptions) => shopifyFetch<T>(url, { method: 'DELETE', ...options });
